/**
 * AppShell -- composition root for the Capicua dashboard.
 *
 * Assembles providers in the canonical nesting order documented in
 * specs/integration-plan.md Section 6.4 (T366-T367):
 *
 *   ToastProvider > Router > AuthGate > UpdateBannerProvider >
 *   CommandPaletteProvider > ShortcutsProvider > ConvexErrorBoundary >
 *   PageShell > optional SideRail > AppRoutes
 *
 * FrictionRecorder mounted inside PageShell for Operator role only.
 *
 * Route classification:
 *   - Workspace-scoped: /workspace/<slug>/... -> mounts SideRail
 *   - Sign-in / OAuth:  /sign-in             -> no PageShell chrome (no TopNav, no SideRail, no Footer)
 *
 * The Footer renders on every non-auth page (v2 design) via PageShell showFooter.
 *
 * Consumes only CSS custom-property references (var(--*)) for colour, font,
 * spacing, radius, and focus-ring.
 */
import React from "react";
import { ToastProvider } from "./components/providers/ToastProvider";
import { Router, useHashLocation } from "./lib/router";
import { AuthGate } from "./components/layout/AuthGate";
import { UpdateBannerProvider } from "./components/providers/UpdateBannerProvider";
import { CommandPaletteProvider } from "./components/providers/CommandPaletteProvider";
import { ShortcutsProvider } from "./components/shortcuts/ShortcutsProvider";
import { ConvexErrorBoundary } from "./components/layout/ConvexErrorBoundary";
import { I18nProvider } from "./i18n/I18nProvider";
import { useFeatureFlag } from "./api/hooks/useFeatureFlag";
import { PageShell } from "./components/layout/PageShell";
import { SideRail } from "./components/layout/SideRail";
import { AppRoutes } from "./AppRoutes";
import { FrictionRecorder } from "./components/layout/FrictionRecorder";
import { useRum } from "./lib/useRum";

/* ------------------------------------------------------------------ */
/*  Route classification helpers                                       */
/* ------------------------------------------------------------------ */

/** Workspace-scoped route: /workspace/<slug>/... */
const WORKSPACE_RE = /^\/workspace\/([^/]+)/;

/** Auth routes that skip PageShell chrome: /sign-in, /oauth-callback */
function isAuthRoute(path: string): boolean {
  return path === "/sign-in" || path.startsWith("/sign-in/") || path === "/oauth-callback";
}

/* ------------------------------------------------------------------ */
/*  Inner shell (needs access to useHashLocation inside Router)        */
/* ------------------------------------------------------------------ */

function AppShellInner() {
  const [path] = useHashLocation();

  // FR-026: Collect Core Web Vitals and ship to /api/rum
  useRum(path);

  const workspaceMatch = WORKSPACE_RE.exec(path);
  const workspaceSlug = workspaceMatch?.[1] ?? null;
  const isAuth = isAuthRoute(path);
  // v2 design: the footer appears on every page (not just marketing routes).
  // Auth routes skip PageShell entirely below, so they never get it.
  const showFooter = true;

  /* On auth routes, skip PageShell entirely -- no TopNav, no chrome. */
  if (isAuth) {
    return <AppRoutes />;
  }

  return (
    <PageShell showFooter={showFooter}>
      {workspaceSlug !== null && <SideRail workspaceSlug={workspaceSlug} />}
      <AppRoutes />
      <FrictionRecorder />
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/*  AppShell -- canonical provider nesting                             */
/* ------------------------------------------------------------------ */

export function AppShell() {
  const i18nEnabled = useFeatureFlag("i18n-enabled") ?? false;

  return (
    <ToastProvider>
      <Router>
        <AuthGate bypassPaths={["/public", "/signin", "/auth/callback"]}>
          <UpdateBannerProvider>
            <CommandPaletteProvider>
              <ShortcutsProvider>
                <ConvexErrorBoundary>
                  <I18nProvider enabled={i18nEnabled}>
                    <AppShellInner />
                  </I18nProvider>
                </ConvexErrorBoundary>
              </ShortcutsProvider>
            </CommandPaletteProvider>
          </UpdateBannerProvider>
        </AuthGate>
      </Router>
    </ToastProvider>
  );
}

export default AppShell;
